import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SUPPORTED_LANGUAGES, translations } from './translations';
import { localizeReaderText } from './readerTerminology';
import { useCookieConsent } from '../context/CookieConsentContext';

const STORAGE_KEY = 'fate-room-language';
const supportedCodes = new Set(SUPPORTED_LANGUAGES.map(({ code }) => code));

const readInitialLanguage = (allowStoredPreference = false) => {
  if (typeof window === 'undefined') return 'en';
  if (allowStoredPreference) {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (supportedCodes.has(stored)) return stored;
  }
  const browserLanguage = String(window.navigator.language || 'en').split('-')[0].toLowerCase();
  return supportedCodes.has(browserLanguage) ? browserLanguage : 'en';
};

const getValue = (source, path) => path.split('.').reduce((value, key) => value?.[key], source);

const I18nContext = createContext({
  language: 'en',
  locale: 'en-GB',
  setLanguage: () => undefined,
  t: (key) => key,
});

const localeByLanguage = {
  en: 'en-GB',
  sk: 'sk-SK',
  cs: 'cs-CZ',
  es: 'es-ES',
  pt: 'pt-PT',
};

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readInitialLanguage);
  const { preferences, consentLoaded } = useCookieConsent();

  useEffect(() => {
    if (!consentLoaded || typeof window === 'undefined') return;
    if (preferences) {
      setLanguageState(readInitialLanguage(true));
      return;
    }
    window.localStorage.removeItem(STORAGE_KEY);
  }, [consentLoaded, preferences]);

  const setLanguage = useCallback((nextLanguage) => {
    const safeLanguage = supportedCodes.has(nextLanguage) ? nextLanguage : 'en';
    setLanguageState(safeLanguage);
    if (preferences) window.localStorage.setItem(STORAGE_KEY, safeLanguage);
  }, [preferences]);

  useEffect(() => {
    const selected = translations[language] || translations.en;
    document.documentElement.lang = language;
    document.title = selected.meta.title;
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement('meta');
      description.name = 'description';
      document.head.appendChild(description);
    }
    description.content = selected.meta.description;
  }, [language]);

  const value = useMemo(() => ({
    language,
    locale: localeByLanguage[language] || localeByLanguage.en,
    setLanguage,
    t: (key) => localizeReaderText(
      getValue(translations[language], key) ?? getValue(translations.en, key) ?? key,
      language,
    ),
  }), [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
